import {
  DeserializedAppReview,
  DeserializedAppSuggestion,
  DeserializedAppTrustReview,
  DeserializedPublicUser,
  DeserializedReviewUser,
  DeserializedUserPublicProfile,
} from '../api';
import { ReviewContentReviewData } from '../api/types/review';

export type AppReview =
  | DeserializedAppTrustReview
  | DeserializedAppReview
  | DeserializedAppSuggestion
  | DeserializedReviewUser;

export const isAppReview = (review: AppReview): review is DeserializedAppReview => {
  return review.type === 'REVIEW';
};

export const isAppTrustReview = (review: AppReview): review is DeserializedAppTrustReview => {
  return review.type === 'TRUST_REVIEW';
};

export const isAppSuggestionReview = (review: AppReview): review is DeserializedAppSuggestion => {
  return review.type === 'SUGGESTION';
};

export const isUserAppReview = (review: AppReview): review is DeserializedReviewUser => {
  return !isAppReview(review) && !isAppTrustReview(review) && !isAppSuggestionReview(review);
};

const transformAppReview = ({
  id,
  attributes,
  user,
}: DeserializedAppReview): ReviewContentReviewData => {
  const {
    text,
    timestamp,
    rating,
    title,
    positiveVotes = 0,
    funnyVotes = 0,
    negativeVotes = 0,
    userFunnyVote = false,
    userVote = 0,
  } = attributes;

  return {
    id,
    text,
    type: 'review',
    title,
    rating,
    timestamp,
    funnyVotes,
    negativeVotes,
    positiveVotes,
    user,
    userFunnyVote,
    userVote,
  };
};

const transformAppTrustReview = ({
  id,
  attributes,
  user,
}: DeserializedAppTrustReview): ReviewContentReviewData => {
  const {
    text,
    timestamp,
    rating,
    title,
    funnyVotes = 0,
    negativeVotes = 0,
    positiveVotes = 0,
    userFunnyVote = false,
    userVote = 0,
  } = attributes;

  return {
    id,
    text,
    title,
    type: 'trust-review',
    trustScore: rating,
    timestamp,
    funnyVotes,
    negativeVotes,
    positiveVotes,
    userFunnyVote,
    userVote,
    user,
  };
};

const transformAppSuggestionReview = ({
  id,
  attributes,
  user,
}: DeserializedAppSuggestion): ReviewContentReviewData => {
  const { text, timestamp, title } = attributes;

  return {
    id,
    text,
    type: 'suggestion',
    title,
    timestamp,
    user,
    // added to comply with type, because suggestion will should not have such fields
    funnyVotes: 0,
    negativeVotes: 0,
    positiveVotes: 0,
    userFunnyVote: false,
    userVote: 0,
  };
};

const transformUserAppReview = ({
  review,
  user,
}: {
  review: DeserializedReviewUser;
  user?: DeserializedUserPublicProfile | undefined;
}): ReviewContentReviewData => {
  const {
    title,
    timestamp,
    text,
    create_time: createTime,
    trustScore,
    rating,
    funnyVotes = 0,
    negativeVotes = 0,
    positiveVotes = 0,
    userFunnyVote = false,
    userVote = 0,
  } = review.attributes;

  return {
    id: review.id,
    type: review.type,
    title,
    text,
    timestamp: createTime || timestamp || '0',
    trustScore: trustScore || undefined,
    rating,
    funnyVotes,
    negativeVotes,
    positiveVotes,
    userFunnyVote,
    userVote,
    user: {
      id: user?.id ?? -1,
      attributes: {
        magicId: user?.attributes.magicId || '',
        name: user?.attributes.displayName || '',
        description: user?.attributes.description || '',
        icon: user?.attributes.avatar || '',
        karma: user?.attributes.karmaPoints || 0,
        membership: {
          tier: user?.membershipTier?.attributes?.key ?? 'FREE',
        },
      },
    } as DeserializedPublicUser,
    app: review.app,
  };
};

export const transformReview = ({
  review,
  user,
}: {
  review: AppReview;
  user?: DeserializedUserPublicProfile;
}): ReviewContentReviewData => {
  if (isAppReview(review)) {
    return transformAppReview(review);
  } else if (isAppSuggestionReview(review)) {
    return transformAppSuggestionReview(review);
  } else if (isAppTrustReview(review)) {
    return transformAppTrustReview(review);
  }

  return transformUserAppReview({ review, user });
};
